import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeSlash, ArrowLeft } from "@phosphor-icons/react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { GoogleButton } from "../components/GoogleButton";

function PasswordField({ value, onChange, placeholder, testid, minLength }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input data-testid={testid} className="kinput pr-12" type={show ? "text" : "password"}
        placeholder={placeholder} value={value} onChange={onChange} required minLength={minLength} />
      <button type="button" data-testid={`${testid}-toggle`} onClick={() => setShow((s) => !s)}
        aria-label={show ? "Masquer le mot de passe" : "Afficher le mot de passe"}
        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-[#16233D] transition-colors">
        {show ? <EyeSlash size={20} /> : <Eye size={20} />}
      </button>
    </div>
  );
}

function ForgotPassword({ onBack }) {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [masked, setMasked] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const sendCode = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/password/forgot", { email });
      setMasked(data.email_masked || ""); setStep("code");
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };

  const doReset = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post("/auth/password/reset", { email, code, password });
      setStep("done");
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };

  if (step === "done") {
    return (
      <div className="kcard p-6 space-y-4" data-testid="forgot-done">
        <h1 className="font-display font-bold text-xl">Mot de passe réinitialisé</h1>
        <p className="text-sm text-gray-600">Vous pouvez maintenant vous connecter avec votre nouveau mot de passe. Vous seul le connaissez.</p>
        <button data-testid="forgot-back-to-login-btn" className="btn-amber w-full py-3" onClick={onBack}>Retour à la connexion</button>
      </div>
    );
  }

  return (
    <form onSubmit={step === "email" ? sendCode : doReset} className="kcard p-6 space-y-4" data-testid="forgot-form">
      <button type="button" onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500"><ArrowLeft size={15} /> Connexion</button>
      <h1 className="font-display font-bold text-xl">Mot de passe oublié</h1>
      {error && <p data-testid="forgot-error" className="text-sm text-[#B4432F] bg-[#F6E3DF] rounded-xl px-3 py-2">{error}</p>}
      {step === "email" ? (
        <>
          <p className="text-sm text-gray-600">Saisissez votre email. Un code de vérification vous sera envoyé par email.</p>
          <input data-testid="forgot-email-input" className="kinput" type="email" placeholder="Votre email"
            value={email} onChange={(e) => setEmail(e.target.value)} required />
          <button data-testid="forgot-send-btn" className="btn-amber w-full py-3" disabled={busy}>{busy ? "Envoi..." : "Recevoir le code"}</button>
        </>
      ) : (
        <>
          <p data-testid="forgot-sent-notice" className="text-sm bg-[#F6F4EF] text-gray-600 rounded-xl px-3 py-2">
            Si un compte existe avec cette adresse, un code vient d'être envoyé à {masked || "votre email"}. Il est valable 15 minutes.
          </p>
          <input data-testid="forgot-code-input" className="kinput" placeholder="Code à 6 chiffres"
            value={code} onChange={(e) => setCode(e.target.value)} required />
          <PasswordField testid="forgot-password-input" placeholder="Nouveau mot de passe (6 car. min)" minLength={6}
            value={password} onChange={(e) => setPassword(e.target.value)} />
          <button data-testid="forgot-reset-btn" className="btn-amber w-full py-3" disabled={busy}>{busy ? "Validation..." : "Définir mon mot de passe"}</button>
        </>
      )}
    </form>
  );
}

export default function Login() {
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(params.get("erreur") || "");
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [notice] = useState(() => {
    const n = sessionStorage.getItem("kavizio_session_notice");
    sessionStorage.removeItem("kavizio_session_notice");
    return n || "";
  });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data);
      navigate("/accueil");
    } catch (err) {
      setError(apiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-md fade-in">
        <p className="font-display font-bold text-4xl lowercase tracking-tight text-center mb-2">kavizio</p>
        <p className="text-center text-sm text-gray-500 mb-8">Votre entreprise, sous contrôle.</p>
        {forgot ? <ForgotPassword onBack={() => setForgot(false)} /> : (
          <form onSubmit={submit} className="kcard p-6 space-y-4">
            <h1 className="font-display font-bold text-xl">Connexion</h1>
            {notice && <p data-testid="session-closed-notice" className="text-sm text-[#8a6a14] bg-[#F7EFD9] rounded-xl px-3 py-2">{notice}</p>}
            {error && <p data-testid="login-error" className="text-sm text-[#B4432F] bg-[#F6E3DF] rounded-xl px-3 py-2">{error}</p>}
            <input data-testid="login-email-input" className="kinput" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            <PasswordField testid="login-password-input" placeholder="Mot de passe" value={password} onChange={(e) => setPassword(e.target.value)} />
            <div className="text-right">
              <button type="button" data-testid="forgot-password-link" onClick={() => setForgot(true)}
                className="text-sm text-[#C9793A] font-semibold hover:underline">Mot de passe oublié ?</button>
            </div>
            <button data-testid="login-submit-btn" className="btn-amber w-full py-3" disabled={busy}>{busy ? "Connexion..." : "Se connecter"}</button>
            <GoogleButton />
            <p className="text-sm text-center text-gray-500">
              Pas encore de compte ? <Link data-testid="goto-register-link" to="/inscription" className="text-[#C9793A] font-semibold">Créer mon entreprise</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
